import { forwardRef, type ComponentRef } from "react";

import * as Popover from "@radix-ui/react-popover";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type PopoverProps = Popover.PopoverProps;

const Root = Popover.Root;

Root.displayName = "Popover";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = Popover.PopoverTriggerProps;

const Trigger = Popover.Trigger;

Trigger.displayName = "PopoverTrigger";

/* -------------------------------------------------------------------------------------------------
 * Anchor
 * -----------------------------------------------------------------------------------------------*/

type AnchorProps = Popover.PopoverAnchorProps;

const Anchor = Popover.Anchor;

Anchor.displayName = "PopoverAnchor";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

interface ContentProps extends Popover.PopoverContentProps {
  forceMount?: true;
}

const Content = forwardRef<ComponentRef<typeof Popover.Content>, ContentProps>(
  ({ forceMount, className, ...props }, forwardedRef) => {
    return (
      <Popover.Portal forceMount={forceMount}>
        <Popover.Content
          {...props}
          ref={forwardedRef}
          className={cn(
            "p-5 z-1400 rounded-[10px] bg-primary-white shadow-[var(--shadow-popover)]",
            "data-[state=open]:data-[side=bottom]:animate-popover-in-bottom",
            "data-[state=open]:data-[side=top]:animate-popover-in-top",
            "data-[state=open]:data-[side=left]:animate-popover-in-left",
            "data-[state=open]:data-[side=right]:animate-popover-in-right",
            "data-[state=closed]:animate-popover-out",
            className,
          )}
        />
      </Popover.Portal>
    );
  },
);

Content.displayName = "PopoverContent";

/* -------------------------------------------------------------------------------------------------
 * Arrow
 * -----------------------------------------------------------------------------------------------*/

type ArrowProps = Popover.PopoverArrowProps;

const Arrow = forwardRef<ComponentRef<typeof Popover.Arrow>, ArrowProps>(({ className, ...props }, forwardedRef) => (
  <Popover.Arrow {...props} ref={forwardedRef} className={cn("fill-primary-white", className)} />
));

Arrow.displayName = "PopoverArrow";

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/

type CloseProps = Popover.PopoverCloseProps;

const Close = Popover.Close;

Close.displayName = "PopoverClose";

export { Anchor, Arrow, Close, Content, Root, Trigger };
export type { AnchorProps, ArrowProps, CloseProps, ContentProps, PopoverProps, TriggerProps };
